"use client";

import { create } from "zustand";
import { persist } from "zustand/middleware";
import dayjs from "dayjs";


import type { ICatFormData, ICatEntry } from "../../shared/types";
import {
  defaultDelivery,
  defaultSubscription,
  defaultUser,
} from "../../shared/constants";
import { getRehydratedSubscriptionUpdate } from "../../utils";
import { buildBaseStoreSlice } from "../../shared/storeHelpers";

import { TargetUser, type IDirectSalesSignupFlowStore } from "../types";
import {
  defaultDirectSalesViewState,
  DIRECT_SALES_FLOW_TTL_DAYS,
} from "../constants";
import { useDirectSalesFlowErrorStore } from "./useDirectSalesFlowErrorStore";
import {
  DEFAULT_DIRECT_SALES_QUIZ_VERSION,
} from "@api-clients/suf-quiz-answers";

/**
 * Zustand store for managing direct sales sign-up flow data: cats, user, delivery, and subscription.
 */
export const useDirectSalesFlowStore = create<IDirectSalesSignupFlowStore>()(
  persist(
    (set, get) => ({
      ...buildBaseStoreSlice(set, get),

      createCat: (id, cat) => set((state) => {
        if (state.cats[id]) { return {}; }
        return {
          cats: {
            ...state.cats,
            [id]: { ...cat },
          },
          viewState: {
            ...state.viewState,
          },
        };
      }),

      createCats: (entries: ICatEntry[]) => set((state) => {
        const updatedCats: Record<string, ICatFormData> = { ...state.cats };
        const incomingIds = new Set(entries.map((e) => e.id));

        // Remove cats that are no longer in the incoming entries
        for (const existingId of Object.keys(updatedCats)) {
          if (!incomingIds.has(existingId)) {
            delete updatedCats[existingId];
          }
        }

        // Add/update cats
        for (const { id, cat } of entries) {
          updatedCats[id] = {
            ...updatedCats[id],
            ...cat,
          };
        }

        const firstCatId = entries[0]?.id ?? "";

        return {
          cats: updatedCats,
          activeCatId: firstCatId,
          viewState: {
            ...state.viewState,
          },
        };
      }),

      /* --- Card Reader ---*/
      cardReaderId: "",
      setCardReaderId: (id: string) => set(() => ({
        cardReaderId: id,
      })),

      /* --- Target user ---*/
      targetUser: TargetUser.CUSTOMER,
      setTargetUser: (target: TargetUser) => set(() => ({
        targetUser: target,
      })),

      /* --- View State ---*/
      viewState: defaultDirectSalesViewState,
      // TODO: Implement DS-specific view state updates as sections are defined.
      updateViewState: () => {},

      /* --- Reset ---*/
      resetSignupFlow: () => set({
        cats: {},
        activeCatId: "",
        user: defaultUser,
        delivery: defaultDelivery,
        // @TODO: when working on reset flow button don't use default discount
        subscription: defaultSubscription,
        viewState: defaultDirectSalesViewState,
        sufQuizVersion: DEFAULT_DIRECT_SALES_QUIZ_VERSION,
        versionMismatchDetected: false,
        savedAt: dayjs().format(),
        cardReaderId: "",
        targetUser: TargetUser.CUSTOMER,
      }),

      /* --- Version ---*/
      sufQuizVersion: DEFAULT_DIRECT_SALES_QUIZ_VERSION,

      /* --- TTL ---*/
      savedAt: dayjs().format(),
    }),
    {
      name: "direct-sales-suf-store",
      onRehydrateStorage: () => (state) => {
        if (!state) {
          return;
        }

        // TTL check: clear store if savedAt exists and is older than DIRECT_SALES_FLOW_TTL_DAYS
        if (
          state.savedAt &&
          dayjs(state.savedAt).isBefore(dayjs().subtract(DIRECT_SALES_FLOW_TTL_DAYS, "day"))
        ) {
          state.resetSignupFlow();
          useDirectSalesFlowErrorStore.getState().clearAll();
          queueMicrotask(() => state.setIsStoreInitialised(true));
          return;
        }

        const subscriptionUpdate = getRehydratedSubscriptionUpdate(state.subscription);
        if (subscriptionUpdate) {
          state.updateSubscription(subscriptionUpdate);
        }

        // Check for version mismatch and set flag (don't reset here)
        // Reset logic is handled explicitly in:
        // 1. Abandoned cart page (checks flag before restore, resets on error)
        // 2. Layout useEffect (checks flag and resets when needed)
        if (state.sufQuizVersion !== DEFAULT_DIRECT_SALES_QUIZ_VERSION) {
          state.setVersionMismatchDetected(true);
        }

        // Defer savedAt reset and setIsStoreInitialised so sync persist doesn't overwrite them.
        queueMicrotask(() => {
          useDirectSalesFlowStore.setState({ savedAt: dayjs().format() });
          state.setIsStoreInitialised(true);
        });
      },
    },
  ),
);
